import { Location, NavigateFunction } from 'react-router-dom';
import { normalizeSearchString, stableSearchParamsString } from './urlCodec';
import {
  CHECKOUT_FLOW_KEY,
  CHECKOUT_RETURN_KEYS,
  CHECKOUT_RETURN_FLAG_KEYS,
  CHECKOUT_SESSION_KEY,
  hasCheckoutReturnParams,
} from './checkoutReturn';

export type HistoryMode = 'push' | 'replace';

type ParamValue = string | number | boolean | null | undefined;

export interface CommitOwnedSearchOptions {
  location: Location;
  navigate: NavigateFunction;
  ownedKeys: string[];
  updates: Record<string, ParamValue>;
  mode?: HistoryMode;
  preserveKeys?: string[];
  pathname?: string;
}

const toParamString = (value: ParamValue): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? '1' : '0';
  const stringValue = `${value}`.trim();
  return stringValue.length > 0 ? stringValue : null;
};

export { hasCheckoutReturnParams };

export const copyCheckoutReturnParams = (source: URLSearchParams, target: URLSearchParams): void => {
  let copiedReturnFlag = false;
  CHECKOUT_RETURN_FLAG_KEYS.forEach((key) => {
    const value = source.get(key);
    if (value !== null) {
      target.set(key, value);
      copiedReturnFlag = true;
    }
  });

  const sessionId = source.get(CHECKOUT_SESSION_KEY);
  if (copiedReturnFlag && sessionId) {
    target.set(CHECKOUT_SESSION_KEY, sessionId);
  }
  const checkoutFlow = source.get(CHECKOUT_FLOW_KEY);
  if (copiedReturnFlag && checkoutFlow) {
    target.set(CHECKOUT_FLOW_KEY, checkoutFlow);
  }
};

export const commitOwnedSearch = ({
  location,
  navigate,
  ownedKeys,
  updates,
  mode = 'replace',
  preserveKeys = [],
  pathname,
}: CommitOwnedSearchOptions): string => {
  const current = new URLSearchParams(location.search);
  const ownedKeySet = new Set(ownedKeys);
  const preserveSet = new Set(preserveKeys);
  const checkoutReturnKeySet = new Set<string>(CHECKOUT_RETURN_KEYS);

  const next = new URLSearchParams();

  current.forEach((value, key) => {
    if (checkoutReturnKeySet.has(key) && !preserveSet.has(key)) {
      return;
    }
    if (!ownedKeySet.has(key) || preserveSet.has(key)) {
      next.append(key, value);
    }
  });

  Object.entries(updates).forEach(([key, rawValue]) => {
    if (!ownedKeySet.has(key)) {
      return;
    }
    const value = toParamString(rawValue);
    if (value !== null) {
      next.set(key, value);
    }
  });

  const nextSearch = stableSearchParamsString(next);
  const normalizedCurrentSearch = normalizeSearchString(location.search);
  const nextPathname = pathname || location.pathname;

  if (nextPathname === location.pathname && nextSearch === normalizedCurrentSearch) {
    return nextSearch;
  }

  navigate(
    {
      pathname: nextPathname,
      search: nextSearch ? `?${nextSearch}` : '',
    },
    { replace: mode === 'replace' }
  );

  return nextSearch;
};
